import { PackageCheck } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { Button, ConfirmDialog, PageHeader } from '@platform/design-system';
import { errorMessageKey } from '@platform/i18n';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import type { BulkResults } from '../orders/bulk.ts';
import type { OrderFilters } from '../orders/order-filters.ts';
import type { Order } from '../orders/orders.ts';
import { STOCK_RESOURCES } from '../orders/orders.ts';
import { ScanField } from '../ScanField.tsx';
import { ScanOutcome, type ScanResult } from '../ScanOutcome.tsx';
import { carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { ReturnBasketRow } from './ReturnBasketRow.tsx';
import { ReturnCandidates } from './ReturnCandidates.tsx';
import { useReturnSuggestions } from './useReturnSuggestions.ts';
import {
  basketEntryOf,
  isEntryValid,
  receiptsBodyOf,
  scanVerdictOf,
  summaryOf,
  type BasketEntry,
  type ReceiptSummary,
  type ReturnCandidate,
  type ScanVerdict,
} from './returns.ts';
import styles from './Returns.module.css';
import { ReturnsToReceive } from './ReturnsToReceive.tsx';

export type ReturnsPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  filters: OrderFilters;
  onFiltersChange: (filters: OrderFilters) => void;
};

export const ReturnsPage = ({ api, shipping, filters, onFiltersChange }: ReturnsPageProps) => {
  const { t, notify, invalidate, describeError } = usePageContext();
  const carriers = useCarriers(shipping);
  const [basket, setBasket] = useState<readonly BasketEntry[]>([]);
  const [choices, setChoices] = useState<readonly ReturnCandidate[]>([]);
  const [outcome, setOutcome] = useState<ScanResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [received, setReceived] = useState<readonly ReceiptSummary[]>([]);
  const [typed, setTyped] = useState('');
  const [scanKey, setScanKey] = useState(0);
  const suggestions = useReturnSuggestions(api, typed);
  const suggestionStatus = (): string => {
    if (suggestions.searching) return t('returns.searching');
    if (suggestions.candidates.length === 0) return t('returns.noSuggestion');
    return t('returns.suggestionsCount', { count: suggestions.candidates.length });
  };
  const inBasket = new Set(basket.map((entry) => entry.order.id));
  const carrierName = (code: string | null): string =>
    code === null ? '—' : carrierNameOf(carriers.data?.items, code);
  const statusOf = (order: Order): string => t(`orders.status_${order.status}`);

  const add = (entries: readonly BasketEntry[]) => {
    setChoices([]);
    setBasket([
      ...entries,
      ...basket.filter((entry) => !entries.some((added) => added.order.id === entry.order.id)),
    ]);
  };
  const addCandidate = (candidate: ReturnCandidate) => {
    add([basketEntryOf(candidate.order, candidate.announcedByCarrier)]);
    setOutcome({ tone: 'accepted', message: t('returns.added', { number: candidate.order.number }) });
  };
  const messageOf = (code: string, verdict: Exclude<ScanVerdict, { kind: 'add' | 'choose' }>): string => {
    if (verdict.kind === 'notFound') return t('returns.notFound', { code });
    if (verdict.kind === 'already')
      return t('returns.alreadyScanned', { number: verdict.candidate.order.number });
    return t('returns.notReceivable', {
      number: verdict.candidate.order.number,
      status: statusOf(verdict.candidate.order),
    });
  };
  const lookUp = useMutation({
    mutationFn: (code: string) => api.get('/admin/returns/parcels', { query: { code } }),
    onSuccess: ({ items }, code) => {
      const verdict = scanVerdictOf(items, inBasket);
      if (verdict.kind === 'add') {
        addCandidate(verdict.candidate);
        return;
      }
      if (verdict.kind === 'choose') {
        setOutcome({
          tone: 'accepted',
          message: t('returns.chooseHelp', { count: verdict.candidates.length }),
        });
        setChoices(verdict.candidates);
        return;
      }
      setChoices([]);
      setOutcome({ tone: 'refused', message: messageOf(code, verdict) });
    },
    onError: (error, code) => {
      setOutcome({ tone: 'refused', message: `${code} : ${describeError(error) ?? ''}` });
    },
  });

  const settle = async (results: BulkResults) => {
    const byOrder = new Map(results.map((result) => [result.orderId, result]));
    const done = basket.filter((entry) => byOrder.get(entry.order.id)?.outcome === 'done');
    setBasket(
      basket
        .filter((entry) => byOrder.get(entry.order.id)?.outcome !== 'done')
        .map((entry) => {
          const code = byOrder.get(entry.order.id)?.code;
          return code === undefined || code === null
            ? entry
            : { ...entry, refusal: t(errorMessageKey(code)) };
        }),
    );
    setReceived([...done.map(summaryOf), ...received]);
    setOutcome({
      tone: done.length === results.length ? 'accepted' : 'refused',
      message: t('returns.receivedCount', { count: done.length, refused: results.length - done.length }),
    });
    if (done.length > 0) notify(t('returns.receivedCount', { count: done.length, refused: 0 }));
    await invalidate(...STOCK_RESOURCES);
  };
  const receive = useMutation({
    mutationFn: (entries: readonly BasketEntry[]) =>
      api.post('/admin/returns/receipts', { body: receiptsBodyOf(entries) }),
    onSuccess: ({ results }) => settle(results),
  });
  const unannounced = basket.filter((entry) => !entry.announced);
  const ready = basket.length > 0 && basket.every(isEntryValid);
  const submit = () => {
    setConfirming(false);
    receive.mutate(basket);
  };

  return (
    <>
      <PageHeader title={t('nav.returns')} context={t('returns.context')} />
      <div className={styles['panel']}>
        <div className={styles['card']}>
          <ScanField
            key={scanKey}
            label={t('returns.scanLabel')}
            helperText={t('returns.scanHelp')}
            submitLabel={t('returns.find')}
            busy={lookUp.isPending || receive.isPending}
            onScan={(code) => {
              lookUp.mutate(code);
            }}
            onTextChange={setTyped}
          />
          <ScanOutcome result={outcome} />
          {choices.length > 0 || !suggestions.active ? null : (
            <div className={styles['suggestions']}>
              <p className={styles['muted']} role="status">
                {suggestionStatus()}
              </p>
              <ReturnCandidates
                label={t('returns.suggestionsTitle')}
                candidates={suggestions.candidates}
                inBasket={inBasket}
                statusOf={statusOf}
                onAdd={(candidate) => {
                  addCandidate(candidate);
                  setTyped('');
                  setScanKey(scanKey + 1);
                }}
              />
            </div>
          )}
          {choices.length === 0 ? null : (
            <ReturnCandidates
              label={t('returns.chooseTitle')}
              candidates={choices}
              inBasket={inBasket}
              statusOf={statusOf}
              onAdd={addCandidate}
            />
          )}
        </div>
        <section aria-labelledby="returns-basket-title" className={styles['card']}>
          <h2 id="returns-basket-title">{t('returns.basketTitle', { count: basket.length })}</h2>
          {basket.length === 0 ? (
            <p className={styles['muted']}>{t('returns.basketEmpty')}</p>
          ) : (
            <ul className={styles['received']}>
              {basket.map((entry) => (
                <ReturnBasketRow
                  key={entry.order.id}
                  entry={entry}
                  carrierName={carrierName(entry.order.dispatch.carrierCode)}
                  onCountsChange={(counts) => {
                    setBasket(
                      basket.map((kept) => (kept.order.id === entry.order.id ? { ...kept, counts } : kept)),
                    );
                  }}
                  onRemove={() => {
                    setBasket(basket.filter((kept) => kept.order.id !== entry.order.id));
                  }}
                />
              ))}
            </ul>
          )}
          {receive.isError ? <p role="alert">{describeError(receive.error)}</p> : null}
          <div className={styles['submitBar']}>
            <Button
              icon={<PackageCheck size={20} aria-hidden="true" />}
              disabled={!ready || receive.isPending}
              onClick={() => {
                if (unannounced.length === 0) submit();
                else setConfirming(true);
              }}
            >
              {receive.isPending ? t('returns.receiving') : t('returns.receiveAll', { count: basket.length })}
            </Button>
          </div>
        </section>
        <ReturnsToReceive
          api={api}
          shipping={shipping}
          filters={filters}
          onFiltersChange={onFiltersChange}
          inBasket={inBasket}
          carrierName={carrierName}
          onAdd={(orders) => {
            add(orders.map((order) => basketEntryOf(order, true)));
          }}
        />
        <section aria-labelledby="returns-session-title" className={styles['card']}>
          <h2 id="returns-session-title">{t('returns.sessionTitle')}</h2>
          {received.length === 0 ? (
            <p className={styles['muted']}>{t('returns.sessionEmpty')}</p>
          ) : (
            <ul className={styles['received']}>
              {received.map((summary) => (
                <li key={summary.orderId}>{t('returns.received', summary)}</li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {confirming ? (
        <ConfirmDialog
          title={t('returns.confirmTitle', { count: unannounced.length })}
          message={t('returns.confirmMessage', {
            numbers: unannounced.map((entry) => entry.order.number).join(', '),
          })}
          confirmLabel={t('returns.receiveAll', { count: basket.length })}
          cancelLabel={t('returns.confirmCancel')}
          closeLabel={t('common.close')}
          onConfirm={submit}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      ) : null}
    </>
  );
};
